import { BaseModel, enumField, ModelInstance, DateTimeField, ToBackHook, ToFrontHook } from './util';
import { City } from './city';
import { File } from './file';
import { League } from './league';
import { Sport } from './sport';
import { Team } from './team';
import { TournamentSeason } from './tournament-season';

export enum TournamentTypes {
  group = 'group',
  elimination = 'elimination',
  fiba = 'fiba',
  swiss = 'swiss',
}

export enum TournamentStatuses {
  open,
  in_progress,
  closed,
}

export enum TournamentGender {
  male= 1,
  female,
  all
}

export enum BasketballStatisticTypes {
  standard = 1,
  light,
  advanced
}

export enum VolleyballStatisticTypes {
  standard = 1,
  light,
  advanced
}

export enum RugbyStatisticTypes {
  light = 1,
  standard,
}

export enum WaterpoloStatisticTypes {
  standard = 1,
  light,
}

export enum WrestballStatisticTypes {
  standard = 1,
}

export enum GameTimeTypes {
  clean = 1,
  dirty,
}

export enum OvertimeTypes {
  to_score_total = 1,
  to_score_diff,
  time,
  without_overtime,
  to_first_goal,
  to_first_goal_and_bullitts,
  bullitts,
  penalties,
  time_and_penalties,
  time_and_first_goal
}

export enum TimerTypes {
  backward = 1,
  forward,
}

export enum SubstituteManageTypes {
  goalkeeper_only = 1,
  additional_screen,
  one_screen,
  split_screen,
}

@ModelInstance({
  mappingFields: {
    id: 'id',
    gender: 'gender',
    birth_year_from: 'birthYearFrom',
    birth_year_to: 'birthYearTo',
    max_team_players: 'maxTeamPlayers',
    max_game_players: 'maxGamePlayers',
    type: 'type',
    tours_count: 'toursCount',
    periods_count: 'periodsCount',
    period_time: 'periodTime',
    overtime_type: 'overtimeType',
    overtime_time: 'overtimeTime',
    overtime_score: 'overtimeScore',
    game_type: 'gameType',
    timeout_count: 'timeoutCount',
    timeout_time: 'timeoutTime',
    substitute_count: 'substituteCount',
    game_up_to_score: 'gameUpToScore',
    volleyball_score_in_set: 'volleyballScoreInSet',
    volleyball_score_in_last_set: 'volleyballScoreInLastSet',
    manage_team_application: 'manageTeamApplication',
    recruitment_is_open: 'recruitmentIsOpen',
    transfer_is_open: 'transferIsOpen',
    basketball_statistic_type: 'basketballStatisticType',
    volleyball_statistic_type: 'volleyballStatisticType',
    rugby_statistic_type: 'rugbyStatisticType',
    waterpolo_statistic_type: 'waterpoloStatisticType',
    wrestball_statistic_type: 'wrestballStatisticType',
    shot_clock_enabled: 'shotClockEnabled',
    game_time_type: 'gameTimeType',
    with_result_table: 'withResultTable',
    free_substitute_enabled: 'freeSubstituteEnabled',
    substitute_manage_enabled: 'substituteManageEnabled',
    substitute_manage_mode: 'substituteManageMode',
    bonus_points_enabled: 'bonusPointsEnabled',
    minor_penalty_time: 'minorPenaltyTime',
    major_penalty_time: 'majorPenaltyTime',
    misconduct_penalty_time: 'misconductPenaltyTime',
    game_misconduct_penalty_time: 'gameMisconductPenaltyTime',
    match_penalty_time: 'matchPenaltyTime',
    timer_type: 'timerType',
    hide_on_league_main_page: 'hideOnLeagueMainPage',
    wrestler_scrums_count: 'wrestlerScrumsCount',
    wrestlers_count: 'wrestlersCount',
  },
  relation: {
    type: enumField(TournamentTypes),
    gender: enumField(TournamentGender),
    basketballStatisticType: enumField(BasketballStatisticTypes),
    volleyballStatisticType: enumField(VolleyballStatisticTypes),
    rugbyStatisticType: enumField(RugbyStatisticTypes),
    waterpoloStatisticType: enumField(WaterpoloStatisticTypes),
    wrestballStatisticType: enumField(WrestballStatisticTypes),
    gameTimeType: enumField(GameTimeTypes),
    overtimeType: enumField(OvertimeTypes),
    timerType: enumField(TimerTypes),
    substituteManageMode: enumField(SubstituteManageTypes),
  }
})
export class TournamentSettings extends BaseModel {
  private _substituteManageMode: SubstituteManageTypes;

  id: number;
  gender: TournamentGender;
  birthYearFrom: number;
  birthYearTo: number;
  maxTeamPlayers: number;
  maxGamePlayers: number;
  type: TournamentTypes;
  toursCount: number;
  periodsCount: number;
  periodTime: number;
  overtimeType: OvertimeTypes;
  overtimeTime: number;
  overtimeScore: number;
  gameType: string;
  timeoutCount: number;
  timeoutTime: number;
  substituteCount: number;
  gameUpToScore: number;
  volleyballScoreInSet: number;
  volleyballScoreInLastSet: number;
  manageTeamApplication: boolean;
  recruitmentIsOpen: boolean;
  transferIsOpen: boolean;
  basketballStatisticType: BasketballStatisticTypes;
  volleyballStatisticType: VolleyballStatisticTypes;
  rugbyStatisticType: RugbyStatisticTypes;
  waterpoloStatisticType: WaterpoloStatisticTypes;
  wrestballStatisticType: WrestballStatisticTypes;
  shotClockEnabled: boolean;
  gameTimeType: GameTimeTypes;
  withResultTable: boolean;
  freeSubstituteEnabled: boolean;
  bonusPointsEnabled: boolean;
  substituteManageEnabled: boolean;
  minorPenaltyTime: number;
  majorPenaltyTime: number;
  misconductPenaltyTime: number;
  gameMisconductPenaltyTime: number;
  matchPenaltyTime: number;
  timerType: TimerTypes;
  hideOnLeagueMainPage: boolean;
  wrestlerScrumsCount: number;
  wrestlersCount: number;

  get substituteManageMode(): SubstituteManageTypes {
    if (this._substituteManageMode) {
      return this._substituteManageMode;
    }
    if (this.substituteManageEnabled) {
      return SubstituteManageTypes.split_screen;
    }
    return SubstituteManageTypes.goalkeeper_only;
  }

  set substituteManageMode(value: SubstituteManageTypes) {
    this._substituteManageMode = value;
  }

  get genderTitle(): string {
    const yearDiff = new Date().getFullYear() - this.birthYearFrom;
    if (this.gender === TournamentGender.male) {
      if (yearDiff < 15) {
        return 'Мальчики';
      }
      if (yearDiff < 21) {
        return 'Юноши';
      }
      return 'Мужчины';
    }
    if (this.gender === TournamentGender.female) {
      if (yearDiff < 15) {
        return 'Девочки';
      }
      if (yearDiff < 21) {
        return 'Девушки';
      }
      return 'Женщины';
    }
    return 'Смешанный';
  }

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}

@ModelInstance({
  mappingFields: {
    id: 'id',
    team_id: 'teamId',
    tournament_id: 'tournamentId',
    name: 'name',
    logo: 'logo',
    team: 'team',
    points: 'points',
  },
  relation: {
    logo: File,
    team: Team,
  }
})
export class TournamentTeamWinner extends BaseModel {
  id: number;
  teamId: number;
  tournamentId: number;
  name: string;
  logo: File;
  team: Team;
  points: number;

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}

@ModelInstance({
  mappingFields: {
    id: 'id',
    name: 'name',
    alias: 'alias',
    logo: 'logo',
    cover: 'cover',
    cover_mobile: 'coverMobile',
    preview_image: 'previewImage',
    city: 'city',
    sport: 'sport',
    location: 'location',
    description: 'description',
    date: 'date',
    date_end: 'dateEnd',
    price: 'price',
    regulation_files: 'regulationFiles',
    settings: 'settings',
    league: 'league',
    teams_count: 'teamsCount',
    closest_game_datetime: 'closestGameDatetime',
    status: 'status',
    team_winner: 'teamWinner',
    team_second: 'teamSecond',
    team_third: 'teamThird',
    season: 'season',
    season_id: 'seasonId',
    game_price: 'gamePrice',
    season_tournament_id: 'seasonTournamentId',
    sort: 'sort',
  },
  relation: {
    logo: File,
    cover: File,
    coverMobile: File,
    previewImage: File,
    city: City,
    sport: Sport,
    regulationFile: File,
    settings: TournamentSettings,
    date: DateTimeField,
    dateEnd: DateTimeField,
    league: League,
    status: enumField(TournamentStatuses),
    teamWinner: TournamentTeamWinner,
    teamSecond: TournamentTeamWinner,
    teamThird: TournamentTeamWinner,
    season: TournamentSeason,
  }
})
export class Tournament extends BaseModel {
  id: number;
  name: string;
  alias: string;
  logo: File;
  cover: File;
  coverMobile: File;
  previewImage: File;
  city: City;
  sport: Sport;
  location: string;
  description: string;
  date: Date;
  dateEnd: Date;
  price: number;
  regulationFiles: File[];
  settings: TournamentSettings;
  league: League;
  teamsCount: number;
  closestGameDatetime: string;
  status: TournamentStatuses;
  teamWinner: TournamentTeamWinner;
  teamSecond: TournamentTeamWinner;
  teamThird: TournamentTeamWinner;
  season: TournamentSeason;
  seasonId: number;
  gamePrice: number;
  seasonTournamentId: number;
  sort: number;

  getEmptyLogoClass(): string {
    const classList = ['empty-logo'];
    if (this.sport.isBasketball()) {
      classList.push('empty-logo-basketball');
    }
    if (this.sport.isVolleyball()) {
      classList.push('empty-logo-volleyball');
    }
    if (this.sport.isHockey()) {
      classList.push('empty-logo-hockey');
    }
    if (this.sport.isFootball()) {
      classList.push('empty-logo-football');
    }
    if (this.sport.isHandball()) {
      classList.push('empty-logo-handball');
    }
    if (this.sport.isRugby()) {
      classList.push('empty-logo-rugby');
    }
    if (this.sport.isWaterpolo()) {
      classList.push('empty-logo-waterpolo');
    }
    return classList.join(' ');
  }

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}
